import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { settingsDropdownItems } from '@/lib/navigation'
import { cn } from '@/lib/utils'
import { Settings, ChevronDown } from 'lucide-react'

interface SettingsMenuProps {
  className?: string
}

export function SettingsMenu({ className }: SettingsMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, right: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right - window.scrollX
      })
    }
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={cn('relative', className)}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium text-muted-foreground hover:text-foreground"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Settings className="w-4 h-4" />
        <span>Settings</span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', {
          'rotate-180': isOpen
        })} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-72 bg-card border border-border rounded-lg shadow-lg z-[9999]"
          style={{
            top: `${position.top}px`,
            right: `${position.right}px`
          }}
        >
          <div className="py-2">
            {settingsDropdownItems.map((item) => (
              <Link
                key={item.key}
                to={item.href!}
                className="flex items-center space-x-3 px-3 py-2 text-sm hover:bg-muted transition-colors group"
                onClick={() => setIsOpen(false)}
              >
                {item.icon && <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-digestr-500 transition-colors" />}
                <div className="flex-1">
                  <div className="font-medium">{item.label}</div>
                  {item.description && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {item.description}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}